/**
 * Add/Edit Employee Page - Microsoft 365 Style
 * Comprehensive employee registration and management form
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, Button, Input, Text, Textarea, Field } from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { SearchableDropdown } from '../../components/common';
import { ApiClient, newToast } from '../../configurations/index.config';
import { countries } from '../../configurations/countries';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  formSection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
  },
});

export const AddEmployeeFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Array<{ id: string; department_name: string }>>(
    []
  );
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    otherNames: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    maritalStatus: '',
    phone: '',
    email: '',
    homeAddress: '',
    suburb: '',
    region: '',
    nationality: 'Ghanaian',

    // Employment
    departmentId: '',
    roleId: '',

    // Financial
    taxId: '',
    ssnitNumber: '',
    bankName: '',
    branch: '',
    accountName: '',
    accountNumber: '',

    // Next of Kin
    kinFirstName: '',
    kinLastName: '',
    kinPhone: '',
    kinEmail: '',
    kinAddress: '',
    kinSuburb: '',
    kinRegion: '',
    kinOccupation: '',
    kinRelationship: '',
  });

  useEffect(() => {
    fetchDepartments();
    fetchRoles();

    // Check if editing existing employee
    const state = location.state as any;
    if (state?.employee) {
      populateForm(state.employee);
    }
  }, [location]);

  const fetchDepartments = async () => {
    try {
      const response = await ApiClient.get('/get-departments');
      if (response.data.status === 'success') {
        setDepartments(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await ApiClient.get('/get-roles');
      if (response.data.status === 'success') {
        setRoles(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const populateForm = (employee: any) => {
    setFormData({
      firstName: employee.first_name || '',
      otherNames: employee.other_names || '',
      lastName: employee.last_name || '',
      gender: employee.gender || '',
      dateOfBirth: employee.date_of_birth ? employee.date_of_birth.split('T')[0] : '',
      maritalStatus: employee.marital_status || '',
      phone: employee.phone || '',
      email: employee.email || '',
      homeAddress: employee.home_address || '',
      suburb: employee.suburb || '',
      region: employee.region || '',
      nationality: employee.nationality || 'Ghanaian',
      departmentId: employee.department_id || '',
      roleId: employee.role_id || '',
      taxId: employee.tax_id || '',
      ssnitNumber: employee.ssnit_number || '',
      bankName: employee.bank_name || '',
      branch: employee.branch || '',
      accountName: employee.account_name || '',
      accountNumber: employee.account_number || '',
      kinFirstName: employee.kin_first_name || '',
      kinLastName: employee.kin_last_name || '',
      kinPhone: employee.kin_phone || '',
      kinEmail: employee.kin_email || '',
      kinAddress: employee.kin_address || '',
      kinSuburb: employee.kin_suburb || '',
      kinRegion: employee.kin_region || '',
      kinOccupation: employee.kin_occupation || '',
      kinRelationship: employee.kin_relationship || '',
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate 10 required fields per backend requirements
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.gender || !formData.gender.trim()) {
      errors.gender = 'Gender is required';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.roleId || formData.roleId.toString().trim() === '') {
      errors.roleId = 'Role is required';
    }

    if (!formData.nationality || !formData.nationality.trim()) {
      errors.nationality = 'Nationality is required';
    }

    if (!formData.kinFirstName.trim()) {
      errors.kinFirstName = 'Kin first name is required';
    }

    if (!formData.kinLastName.trim()) {
      errors.kinLastName = 'Kin last name is required';
    }

    if (!formData.kinPhone.trim()) {
      errors.kinPhone = 'Kin phone is required';
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      newToast('error', 'Please fill in all required fields correctly');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: (location.state as any)?.employee?.id || null,
        first_name: formData.firstName,
        other_names: formData.otherNames,
        last_name: formData.lastName,
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth,
        marital_status: formData.maritalStatus,
        phone: formData.phone,
        email: formData.email,
        home_address: formData.homeAddress,
        suburb: formData.suburb,
        region: formData.region,
        nationality: formData.nationality,
        department_id: formData.departmentId || null,
        role_id: formData.roleId || null,
        tax_id: formData.taxId,
        ssnit_number: formData.ssnitNumber,
        bank_name: formData.bankName,
        branch: formData.branch,
        account_name: formData.accountName,
        account_number: formData.accountNumber,
        kin_first_name: formData.kinFirstName,
        kin_last_name: formData.kinLastName,
        kin_phone: formData.kinPhone,
        kin_email: formData.kinEmail,
        kin_address: formData.kinAddress,
        kin_suburb: formData.kinSuburb,
        kin_region: formData.kinRegion,
        kin_occupation: formData.kinOccupation,
        kin_relationship: formData.kinRelationship,
      };

      const response = await ApiClient.post('/add-new-employee', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Employee saved successfully');
        navigate('/account/employees');
      } else {
        newToast('error', response.data?.message || 'Failed to save employee');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/employees')}
        >
          Back
        </Button>
        <Text className={styles.pageTitle}>
          {(location.state as any)?.employee ? 'Edit Employee' : 'Add New Employee'}
        </Text>
      </div>

      <ContentCard>
        {/* Personal Information */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Personal Information</Text>

          <div className={styles.formRow}>
            <Field
              label="First Name"
              required
              validationState={validationErrors.firstName ? 'error' : undefined}
              validationMessage={validationErrors.firstName}
            >
              <Input
                value={formData.firstName}
                onChange={e => {
                  setFormData({ ...formData, firstName: e.target.value });
                  if (validationErrors.firstName) {
                    setValidationErrors({ ...validationErrors, firstName: '' });
                  }
                }}
                placeholder="Enter first name"
              />
            </Field>

            <Field label="Other Names">
              <Input
                value={formData.otherNames}
                onChange={e => setFormData({ ...formData, otherNames: e.target.value })}
                placeholder="Enter other names"
              />
            </Field>

            <Field
              label="Last Name"
              required
              validationState={validationErrors.lastName ? 'error' : undefined}
              validationMessage={validationErrors.lastName}
            >
              <Input
                value={formData.lastName}
                onChange={e => {
                  setFormData({ ...formData, lastName: e.target.value });
                  if (validationErrors.lastName) {
                    setValidationErrors({ ...validationErrors, lastName: '' });
                  }
                }}
                placeholder="Enter last name"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field
              label="Gender"
              required
              validationState={validationErrors.gender ? 'error' : undefined}
              validationMessage={validationErrors.gender}
            >
              <SearchableDropdown
                placeholder="Select gender"
                options={[
                  { id: 'Male', label: 'Male' },
                  { id: 'Female', label: 'Female' },
                ]}
                selectedValue={formData.gender}
                onSelect={value => {
                  setFormData({ ...formData, gender: value });
                  if (validationErrors.gender) {
                    setValidationErrors({ ...validationErrors, gender: '' });
                  }
                }}
              />
            </Field>

            <Field label="Date of Birth">
              <Input
                type="date"
                value={formData.dateOfBirth}
                onChange={e => setFormData({ ...formData, dateOfBirth: e.target.value })}
              />
            </Field>

            <Field label="Marital Status">
              <SearchableDropdown
                placeholder="Select status"
                options={[
                  { id: 'Single', label: 'Single' },
                  { id: 'Married', label: 'Married' },
                  { id: 'Divorced', label: 'Divorced' },
                  { id: 'Widowed', label: 'Widowed' },
                ]}
                selectedValue={formData.maritalStatus}
                onSelect={value => setFormData({ ...formData, maritalStatus: value })}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field
              label="Phone"
              required
              validationState={validationErrors.phone ? 'error' : undefined}
              validationMessage={validationErrors.phone}
            >
              <Input
                value={formData.phone}
                onChange={e => {
                  setFormData({ ...formData, phone: e.target.value });
                  if (validationErrors.phone) {
                    setValidationErrors({ ...validationErrors, phone: '' });
                  }
                }}
                placeholder="Enter phone number"
              />
            </Field>

            <Field
              label="Email"
              required
              validationState={validationErrors.email ? 'error' : undefined}
              validationMessage={validationErrors.email}
            >
              <Input
                type="email"
                value={formData.email}
                onChange={e => {
                  setFormData({ ...formData, email: e.target.value });
                  if (validationErrors.email) {
                    setValidationErrors({ ...validationErrors, email: '' });
                  }
                }}
                placeholder="Enter email address"
              />
            </Field>

            <Field
              label="Nationality"
              required
              validationState={validationErrors.nationality ? 'error' : undefined}
              validationMessage={validationErrors.nationality}
            >
              <SearchableDropdown
                placeholder="Search nationality..."
                options={countries.map(country => ({
                  id: country.value,
                  label: country.label,
                }))}
                selectedValue={formData.nationality}
                onSelect={value => {
                  setFormData({ ...formData, nationality: value });
                  if (validationErrors.nationality) {
                    setValidationErrors({ ...validationErrors, nationality: '' });
                  }
                }}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Home Address" style={{ gridColumn: '1 / -1' }}>
              <Textarea
                value={formData.homeAddress}
                onChange={e => setFormData({ ...formData, homeAddress: e.target.value })}
                placeholder="Enter home address"
                rows={2}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Suburb">
              <Input
                value={formData.suburb}
                onChange={e => setFormData({ ...formData, suburb: e.target.value })}
                placeholder="Enter suburb"
              />
            </Field>

            <Field label="Region">
              <Input
                value={formData.region}
                onChange={e => setFormData({ ...formData, region: e.target.value })}
                placeholder="Enter region"
              />
            </Field>
          </div>
        </div>

        {/* Employment Details */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Employment Details</Text>

          <div className={styles.formRow}>
            <Field label="Department">
              <SearchableDropdown
                placeholder="Search department..."
                options={departments.map(dept => ({
                  id: dept.id,
                  label: dept.department_name,
                }))}
                selectedValue={formData.departmentId}
                onSelect={value => setFormData({ ...formData, departmentId: value })}
              />
            </Field>

            <Field
              label="Role"
              required
              validationState={validationErrors.roleId ? 'error' : undefined}
              validationMessage={validationErrors.roleId}
            >
              <SearchableDropdown
                placeholder="Search role..."
                options={roles.map(role => ({
                  id: role.id,
                  label: role.name,
                }))}
                selectedValue={formData.roleId}
                onSelect={value => {
                  setFormData({ ...formData, roleId: value });
                  if (validationErrors.roleId) {
                    setValidationErrors({ ...validationErrors, roleId: '' });
                  }
                }}
              />
            </Field>
          </div>
        </div>

        {/* Financial Information */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Financial Information</Text>

          <div className={styles.formRow}>
            <Field label="Tax ID">
              <Input
                value={formData.taxId}
                onChange={e => setFormData({ ...formData, taxId: e.target.value })}
                placeholder="Enter tax ID"
              />
            </Field>

            <Field label="SSNIT Number">
              <Input
                value={formData.ssnitNumber}
                onChange={e => setFormData({ ...formData, ssnitNumber: e.target.value })}
                placeholder="Enter SSNIT number"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Bank Name">
              <Input
                value={formData.bankName}
                onChange={e => setFormData({ ...formData, bankName: e.target.value })}
                placeholder="Enter bank name"
              />
            </Field>

            <Field label="Branch">
              <Input
                value={formData.branch}
                onChange={e => setFormData({ ...formData, branch: e.target.value })}
                placeholder="Enter branch"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Account Name">
              <Input
                value={formData.accountName}
                onChange={e => setFormData({ ...formData, accountName: e.target.value })}
                placeholder="Enter account name"
              />
            </Field>

            <Field label="Account Number">
              <Input
                value={formData.accountNumber}
                onChange={e => setFormData({ ...formData, accountNumber: e.target.value })}
                placeholder="Enter account number"
              />
            </Field>
          </div>
        </div>

        {/* Next of Kin */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Next of Kin</Text>

          <div className={styles.formRow}>
            <Field
              label="First Name"
              required
              validationState={validationErrors.kinFirstName ? 'error' : undefined}
              validationMessage={validationErrors.kinFirstName}
            >
              <Input
                value={formData.kinFirstName}
                onChange={e => {
                  setFormData({ ...formData, kinFirstName: e.target.value });
                  if (validationErrors.kinFirstName) {
                    setValidationErrors({ ...validationErrors, kinFirstName: '' });
                  }
                }}
                placeholder="Enter kin first name"
              />
            </Field>

            <Field
              label="Last Name"
              required
              validationState={validationErrors.kinLastName ? 'error' : undefined}
              validationMessage={validationErrors.kinLastName}
            >
              <Input
                value={formData.kinLastName}
                onChange={e => {
                  setFormData({ ...formData, kinLastName: e.target.value });
                  if (validationErrors.kinLastName) {
                    setValidationErrors({ ...validationErrors, kinLastName: '' });
                  }
                }}
                placeholder="Enter kin last name"
              />
            </Field>

            <Field label="Relationship">
              <Input
                value={formData.kinRelationship}
                onChange={e => setFormData({ ...formData, kinRelationship: e.target.value })}
                placeholder="e.g., Spouse, Parent, Sibling"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field
              label="Phone"
              required
              validationState={validationErrors.kinPhone ? 'error' : undefined}
              validationMessage={validationErrors.kinPhone}
            >
              <Input
                value={formData.kinPhone}
                onChange={e => {
                  setFormData({ ...formData, kinPhone: e.target.value });
                  if (validationErrors.kinPhone) {
                    setValidationErrors({ ...validationErrors, kinPhone: '' });
                  }
                }}
                placeholder="Enter kin phone"
              />
            </Field>

            <Field label="Email">
              <Input
                type="email"
                value={formData.kinEmail}
                onChange={e => setFormData({ ...formData, kinEmail: e.target.value })}
                placeholder="Enter kin email"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Address" style={{ gridColumn: '1 / -1' }}>
              <Textarea
                value={formData.kinAddress}
                onChange={e => setFormData({ ...formData, kinAddress: e.target.value })}
                placeholder="Enter kin address"
                rows={2}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Suburb">
              <Input
                value={formData.kinSuburb}
                onChange={e => setFormData({ ...formData, kinSuburb: e.target.value })}
                placeholder="Enter kin suburb"
              />
            </Field>

            <Field label="Region">
              <SearchableDropdown
                placeholder="Search region..."
                options={[
                  { id: 'Greater Accra', label: 'Greater Accra' },
                  { id: 'Ashanti', label: 'Ashanti' },
                  { id: 'Northern', label: 'Northern' },
                  { id: 'Western', label: 'Western' },
                  { id: 'Eastern', label: 'Eastern' },
                  { id: 'Central', label: 'Central' },
                  { id: 'Volta', label: 'Volta' },
                  { id: 'Upper East', label: 'Upper East' },
                  { id: 'Upper West', label: 'Upper West' },
                  { id: 'Brong Ahafo', label: 'Brong Ahafo' },
                ]}
                selectedValue={formData.kinRegion}
                onSelect={value => setFormData({ ...formData, kinRegion: value })}
              />
            </Field>

            <Field label="Occupation">
              <Input
                value={formData.kinOccupation}
                onChange={e => setFormData({ ...formData, kinOccupation: e.target.value })}
                placeholder="Enter kin occupation"
              />
            </Field>
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => navigate('/account/employees')}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Employee'}
          </Button>
        </div>
      </ContentCard>
    </div>
  );
};

export default AddEmployeeFluentUI;
